/**
 * 发文记录页面 — PublishRecords（二期批2 翻新，对齐概念稿 publish-records.html）。
 *
 * GET   /api/publish-records?brand_id&product_id&company_id   列表（选择器三级过滤）
 * GET   /api/publish-records?unattributed=1&brand_id&company_id 未归因桶（忽略产品过滤）
 * GET   /api/products?brand_id=                               补标弹窗的产品候选
 * PATCH /api/publish-records/{id}                             补标产品（二期批2 新端点）
 *
 * 结构：4 指标卡 → 分平台发文表现 → 工具栏（归因桶 + 状态桶）→ 记录表。
 * 核心新增（PRD §4.6.4 / R2）：
 * - 「未归因」过滤桶：product_id IS NULL 的记录，桶徽标计数；
 * - 行内补标：未归因行可弹窗选产品调 PATCH，成功后移出未归因桶；
 * - 选择器产品过滤生效（GET 已支持 product_id 参数）。
 *
 * 未归因桶独立取数（缺陷 A 修复）：未归因按定义没有 product_id，若与主列表
 * 共用产品过滤则选中产品后恒为 0。故未归因桶的计数与列表走独立请求
 * （unattributed=1 + brand/company，不带 product_id；后端此时忽略产品过滤），
 * 选中产品时未归因桶仍可展示、可补标；补标成功后本地移出该桶。
 *
 * 概念稿裁剪：收录状态/复检/分渠道收录率属收录台账域（index-ledger），
 * 发文接口无此字段，按"接口没有不虚构"原则跳过，入口跳收录台账。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { LaunchOutlined } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { getPublishRecords, patchPublishRecord } from "../api/publish";
import { getDeliveryCopies } from "../api/copies";
import { getProducts } from "../api/products";
import { useCompanyStore } from "../store/companyStore";
import { COPY_STATUS_MAP, PUBLISH_STATUS_MAP, contentTypeLabel } from "../utils/constants";
import { formatDateTime, formatDate } from "../utils/format";
import type { DeliveryCopy, ProductListItem, PublishRecord } from "../types";

/** 归因桶。 */
type Bucket = "all" | "attributed" | "unattributed";

/** 可点击过滤桶徽标（选中=实色，未选中=灰底）。 */
function BucketChip({
  label,
  count,
  active,
  color,
  bg,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  color: string;
  bg: string;
  onClick: () => void;
}) {
  return (
    <Chip
      label={`${label} ${count}`}
      size="small"
      onClick={onClick}
      sx={{
        fontSize: 11,
        height: 22,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? color : "text.secondary",
        bgcolor: active ? bg : "semanticBg.neutral",
        border: "1px solid",
        borderColor: active ? color : "transparent",
      }}
    />
  );
}

/** 发文状态徽章。 */
function PublishStatusBadge({ status }: { status: string }) {
  const info = PUBLISH_STATUS_MAP[status] || {
    label: status,
    color: "text.secondary",
    bg: "semanticBg.neutral",
  };
  return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
}

export default function PublishRecords() {
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  const [allItems, setAllItems] = useState<PublishRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bucket, setBucket] = useState<Bucket>("all");
  const [statusFilter, setStatusFilter] = useState("");

  // 未归因桶独立取数（缺陷 A）：不带 product_id，不受产品选择器影响
  const [unattributedItems, setUnattributedItems] = useState<PublishRecord[]>([]);
  const [unattrLoading, setUnattrLoading] = useState(true);

  // 补标弹窗
  const [tagRecord, setTagRecord] = useState<PublishRecord | null>(null);
  const [tagProducts, setTagProducts] = useState<ProductListItem[]>([]);
  const [tagProductId, setTagProductId] = useState("");
  const [tagLoading, setTagLoading] = useState(false);
  const [tagSubmitting, setTagSubmitting] = useState(false);
  const [tagError, setTagError] = useState("");

  // 成功提示
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");

  // 详情弹窗（批三B #25）
  const [detailRecord, setDetailRecord] = useState<PublishRecord | null>(null);
  /** 关联文案（#35）：优先按 copy_id 精确直查（GET /api/delivery-copies?copy_id=），
      查不到再退回"同产品 + 标题精确匹配"兜底；都没有则正文区显 "—"。 */
  const [detailCopy, setDetailCopy] = useState<DeliveryCopy | null>(null);
  const [detailCopyLoading, setDetailCopyLoading] = useState(false);

  /** 打开详情弹窗：copy_id 直查优先，标题匹配兜底（两者都没有时跳过）。 */
  useEffect(() => {
    setDetailCopy(null);
    if (!detailRecord) return;
    if (!detailRecord.copy_id && !detailRecord.product_id) return;
    let cancelled = false;
    setDetailCopyLoading(true);

    /** 标题精确匹配兜底（需 product_id）。 */
    const matchByTitle = (): Promise<DeliveryCopy | null> => {
      if (!detailRecord.product_id) return Promise.resolve(null);
      return getDeliveryCopies({ product_id: detailRecord.product_id })
        .then(
          (copies) =>
            copies.find((c) => c.title === detailRecord.content_title) || null,
        )
        .catch(() => null);
    };

    const lookup = detailRecord.copy_id
      ? getDeliveryCopies({ copy_id: detailRecord.copy_id })
          .then((copies) => copies[0] || null)
          .catch(() => null)
          .then((hit) => hit ?? matchByTitle())
      : matchByTitle();

    lookup
      .then((hit) => {
        if (!cancelled) setDetailCopy(hit);
      })
      .finally(() => {
        if (!cancelled) setDetailCopyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailRecord]);

  /** 按选择器三级过滤拉取（product > brand > company，后端可叠加）。 */
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getPublishRecords({
        product_id: selectedProductId || undefined,
        brand_id: selectedBrandId || undefined,
        company_id: selectedCompanyId || undefined,
      });
      setAllItems(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取发文记录失败");
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, selectedBrandId, selectedProductId]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  /**
   * 未归因桶独立拉取（缺陷 A 修复）：
   * 未归因记录 product_id IS NULL，主列表的产品过滤会把它们全部排除，
   * 故桶计数/列表走 unattributed=1 且不带 product_id（保留品牌/公司维度）；
   * 失败仅桶显 0，不阻断主列表。
   */
  const fetchUnattributed = useCallback(async () => {
    setUnattrLoading(true);
    try {
      const result = await getPublishRecords({
        unattributed: "1",
        brand_id: selectedBrandId || undefined,
        company_id: selectedCompanyId || undefined,
      });
      setUnattributedItems(result);
    } catch {
      setUnattributedItems([]);
    } finally {
      setUnattrLoading(false);
    }
  }, [selectedCompanyId, selectedBrandId]);

  useEffect(() => {
    fetchUnattributed();
  }, [fetchUnattributed]);

  /** 本月发文数。 */
  const monthCount = useMemo(() => {
    const now = new Date();
    return allItems.filter((r) => {
      const d = new Date(r.publish_time);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
  }, [allItems]);

  /** 归因统计（已归因按主列表口径；未归因走独立桶请求，不受产品过滤影响）。 */
  const attributedCount = useMemo(
    () => allItems.filter((r) => r.product_id).length,
    [allItems],
  );
  const unattributedCount = unattributedItems.length;

  /** 分平台发文表现（当前选择器范围内按 platform 聚合）。 */
  const platformStats = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of allItems) {
      map.set(r.platform, (map.get(r.platform) || 0) + 1);
    }
    const arr = [...map.entries()]
      .map(([platform, count]) => ({ platform, count }))
      .sort((a, b) => b.count - a.count);
    const max = arr.length > 0 ? arr[0].count : 1;
    return { arr, max };
  }, [allItems]);

  /** 桶 + 状态过滤后的展示集（未归因桶用独立请求的数据）。 */
  const items = useMemo(() => {
    let list =
      bucket === "unattributed"
        ? unattributedItems
        : bucket === "attributed"
          ? allItems.filter((r) => r.product_id)
          : allItems;
    if (statusFilter) list = list.filter((r) => r.status === statusFilter);
    return list;
  }, [allItems, unattributedItems, bucket, statusFilter]);

  /** 打开补标弹窗：加载该发文品牌下的产品候选。 */
  const handleOpenTag = async (record: PublishRecord) => {
    setTagRecord(record);
    setTagProductId("");
    setTagError("");
    setTagLoading(true);
    try {
      const res = await getProducts({ brand_id: record.brand_id, size: 100 });
      setTagProducts(res.items || []);
    } catch {
      setTagProducts([]);
      setTagError("产品候选加载失败，请稍后重试");
    } finally {
      setTagLoading(false);
    }
  };

  /** 提交补标。 */
  const handleSubmitTag = async () => {
    if (!tagRecord || !tagProductId) return;
    setTagSubmitting(true);
    setTagError("");
    try {
      const updated = await patchPublishRecord(tagRecord.id, tagProductId);
      // 本地更新：该行立即移出未归因桶；主列表若在范围内则同步更新
      setUnattributedItems((prev) => prev.filter((r) => r.id !== updated.id));
      setAllItems((prev) =>
        prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
      );
      setSnackMsg(
        `已补标：${updated.content_title} → ${updated.product_name || updated.product_name_snapshot || "产品"}`,
      );
      setSnackOpen(true);
      setTagRecord(null);
    } catch (err) {
      setTagError(err instanceof Error ? err.message : "补标失败，请稍后重试");
    } finally {
      setTagSubmitting(false);
    }
  };

  const columns: DataTableColumn<PublishRecord>[] = [
    {
      key: "title",
      label: "标题",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {row.content_title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            {contentTypeLabel(row.content_type)}
          </Typography>
        </Box>
      ),
    },
    {
      key: "product",
      label: "产品",
      render: (row) => {
        if (row.product_id) {
          return (
            <Typography sx={{ fontSize: 13 }}>
              {row.product_name || row.product_name_snapshot || "（已删除产品）"}
            </Typography>
          );
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Badge label="未归因" variant="warning" />
            {row.product_name_snapshot && (
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                快照：{row.product_name_snapshot}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      key: "platform",
      label: "平台账户",
      render: (row) => (
        <Typography sx={{ fontSize: 13 }}>
          {row.platform}
          {row.platform_account_name ? ` · ${row.platform_account_name}` : ""}
        </Typography>
      ),
    },
    {
      key: "time",
      label: "发布时间",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDateTime(row.publish_time)}
        </Typography>
      ),
    },
    {
      key: "method",
      label: "发布方式",
      align: "center",
      render: (row) => (
        <Typography sx={{ fontSize: 12 }}>{row.publish_method || "—"}</Typography>
      ),
    },
    {
      key: "ghs",
      label: "GHS 变化",
      align: "center",
      render: (row) => {
        const pre = row.pre_publish_ghs;
        const post = row.post_publish_ghs;
        return pre != null && post != null ? (
          <Typography
            sx={{
              fontSize: 12,
              color: post >= pre ? "success.main" : "error.main",
            }}
          >
            {pre} → {post}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>
        );
      },
    },
    {
      key: "rescan",
      label: "计划复诊",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDate(row.scheduled_ghs_rescan)}
        </Typography>
      ),
    },
    {
      key: "status",
      label: "状态",
      align: "center",
      render: (row) => <PublishStatusBadge status={row.status} />,
    },
    {
      key: "action",
      label: "操作",
      align: "center",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setDetailRecord(row)}
            sx={{ fontSize: 12, minWidth: 0, px: 0.5 }}
          >
            查看
          </Button>
          {row.publish_url && (
            <Button
              size="small"
              variant="text"
              endIcon={<LaunchOutlined sx={{ fontSize: 12 }} />}
              href={row.publish_url}
              target="_blank"
              rel="noreferrer"
              sx={{ fontSize: 12, minWidth: 0, px: 0.5 }}
            >
              原文
            </Button>
          )}
          {!row.product_id && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleOpenTag(row)}
              sx={{ fontSize: 12 }}
            >
              补标产品
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="发文记录"
        breadcrumbs={[{ label: "媒体投放" }, { label: "发文记录" }]}
        subtitle="发文以产品为轴归因 · 收录状态与收录台账联动"
        actions={
          <Button
            variant="outlined"
            component={RouterLink}
            to="/index-ledger"
          >
            去收录台账排查
          </Button>
        }
      />

      {/* 4 指标卡 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="发文总数"
            value={loading ? "—" : allItems.length}
            subtitle="当前选择器范围"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="本月发文"
            value={loading ? "—" : monthCount}
            subtitle="按发布时间统计"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="已归因产品"
            value={loading ? "—" : attributedCount}
            subtitle={
              allItems.length > 0
                ? `归因率 ${((attributedCount / allItems.length) * 100).toFixed(1)}%`
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="未归因"
            value={unattrLoading ? "—" : unattributedCount}
            subtitle={
              unattributedCount > 0
                ? "历史数据允许为空 · 可逐条补标（PRD §4.6.4）"
                : "全部发文已归属产品"
            }
          />
        </Grid>
      </Grid>

      {/* 分平台发文表现 */}
      {!loading && !error && platformStats.arr.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography variant="h4">分平台发文表现</Typography>
              <Badge label={`${platformStats.arr.length} 个平台`} />
            </Box>
            <Grid container spacing={2}>
              {platformStats.arr.map((s) => (
                <Grid item xs={12} sm={6} md={4} key={s.platform}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: 12, minWidth: 72, color: "text.secondary" }}>
                      {s.platform}
                    </Typography>
                    <Box
                      sx={{
                        flexGrow: 1,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "semanticBg.neutral",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${(s.count / platformStats.max) * 100}%`,
                          height: "100%",
                          bgcolor: "primary.main",
                          borderRadius: 3,
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 28, textAlign: "right" }}>
                      {s.count}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                lineHeight: 1.8,
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid",
                borderColor: "lineSoft",
              }}
            >
              数值 = 当前范围内发文篇数；收录率与复检进展属收录台账域，
              请点右上角「去收录台账排查」。
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* 工具栏：归因桶 + 状态筛选 */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <BucketChip
              label="全部"
              count={allItems.length}
              active={bucket === "all"}
              color="primary.main"
              bg="brand.50"
              onClick={() => setBucket("all")}
            />
            <BucketChip
              label="已归因"
              count={attributedCount}
              active={bucket === "attributed"}
              color="success.main"
              bg="semanticBg.success"
              onClick={() => setBucket("attributed")}
            />
            <BucketChip
              label="未归因"
              count={unattributedCount}
              active={bucket === "unattributed"}
              color="warning.main"
              bg="semanticBg.warning"
              onClick={() => setBucket("unattributed")}
            />
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              select
              size="small"
              label="状态"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">全部状态</MenuItem>
              <MenuItem value="published">已发布</MenuItem>
              <MenuItem value="scheduled">已排期</MenuItem>
              <MenuItem value="draft">草稿</MenuItem>
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {/* 记录表 */}
      {(bucket === "unattributed" ? unattrLoading : loading) ? (
        <Loading fullPage />
      ) : error ? (
        <Card>
          <CardContent>
            <EmptyState text={error} />
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={items}
          showPagination={false}
          getRowId={(row) => row.id}
          emptyText={
            bucket === "unattributed"
              ? "未归因桶为空 · 全部发文已归属产品"
              : "暂无发文记录"
          }
        />
      )}

      {/* 详情弹窗（批三B #25，照抄 CopyLibrary 详情 Dialog 模式）：
          只渲染接口真实字段——发布信息（publish-records 列表字段）+
          关联文案正文（#35：copy_id 直查优先，标题匹配兜底）+
          收录/台账状态（pre/post GHS、计划复诊；收录率属收录台账域，不虚构）。 */}
      <Dialog
        open={detailRecord !== null}
        onClose={() => setDetailRecord(null)}
        maxWidth="md"
        fullWidth
      >
        {detailRecord && (
          <>
            <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={{ flexGrow: 1 }}>
                  {detailRecord.content_title}
                </Box>
                <PublishStatusBadge status={detailRecord.status} />
              </Box>
            </DialogTitle>
            <DialogContent>
              {/* 发布信息 */}
              <Typography
                sx={{ fontSize: 12, fontWeight: 500, color: "primary.main", mb: 1 }}
              >
                发布信息
              </Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    平台 / 账户
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {detailRecord.platform}
                    {detailRecord.platform_account_name
                      ? ` · ${detailRecord.platform_account_name}`
                      : ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    发布时间
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {formatDateTime(detailRecord.publish_time)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    内容类型
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {contentTypeLabel(detailRecord.content_type)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    发布方式
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {detailRecord.publish_method || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    归属产品
                  </Typography>
                  {detailRecord.product_id ? (
                    <Typography sx={{ fontSize: 13 }}>
                      {detailRecord.product_name ||
                        detailRecord.product_name_snapshot ||
                        "（已删除产品）"}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Badge label="未归因" variant="warning" />
                      {detailRecord.product_name_snapshot && (
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                          快照：{detailRecord.product_name_snapshot}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    发布链接
                  </Typography>
                  {detailRecord.publish_url ? (
                    <Button
                      size="small"
                      variant="text"
                      endIcon={<LaunchOutlined sx={{ fontSize: 12 }} />}
                      href={detailRecord.publish_url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ fontSize: 12, minWidth: 0, px: 0.5 }}
                    >
                      打开原文
                    </Button>
                  ) : (
                    <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                      —
                    </Typography>
                  )}
                </Grid>
              </Grid>
              {Array.isArray(detailRecord.content_keywords) &&
                detailRecord.content_keywords.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                    {detailRecord.content_keywords.map((kw, i) => (
                      <Chip
                        key={i}
                        label={kw}
                        size="small"
                        sx={{ fontSize: 11, bgcolor: "brand.50", color: "primary.main" }}
                      />
                    ))}
                  </Box>
                )}

              <Divider sx={{ my: 2 }} />

              {/* 关联文案正文（#35：copy_id 精确直查优先，标题匹配兜底） */}
              <Typography
                sx={{ fontSize: 12, fontWeight: 500, color: "primary.main", mb: 1 }}
              >
                关联文案正文
              </Typography>
              {detailCopyLoading ? (
                <Loading text="正在匹配关联文案..." />
              ) : detailCopy ? (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", flexGrow: 1 }}>
                      {detailCopy.title}
                      {detailCopy.content_body
                        ? `（${detailCopy.content_body.length.toLocaleString()} 字）`
                        : ""}
                    </Typography>
                    <Badge
                      label={
                        (COPY_STATUS_MAP[detailCopy.status] || {
                          label: detailCopy.status,
                        }).label
                      }
                      color={
                        (COPY_STATUS_MAP[detailCopy.status] || {
                          color: "text.secondary",
                        }).color
                      }
                      bgColor={
                        (COPY_STATUS_MAP[detailCopy.status] || {
                          bg: "semanticBg.neutral",
                        }).bg
                      }
                    />
                  </Box>
                  {detailCopy.content_body ? (
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: "brand.50",
                        borderRadius: 1,
                        maxHeight: 300,
                        overflowY: "auto",
                      }}
                    >
                      <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                        {detailCopy.content_body}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                      该文案无正文内容 —
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                  {detailRecord.copy_id || detailRecord.product_id
                    ? "— 文案库中未找到关联文案"
                    : "— 未归因产品，无法关联文案"}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {/* 收录 / 台账状态 */}
              <Typography
                sx={{ fontSize: 12, fontWeight: 500, color: "primary.main", mb: 1 }}
              >
                收录 / 台账状态
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    GHS 变化（发布前 → 发布后）
                  </Typography>
                  {detailRecord.pre_publish_ghs != null &&
                  detailRecord.post_publish_ghs != null ? (
                    <Typography
                      sx={{
                        fontSize: 13,
                        color:
                          detailRecord.post_publish_ghs >= detailRecord.pre_publish_ghs
                            ? "success.main"
                            : "error.main",
                      }}
                    >
                      {detailRecord.pre_publish_ghs} → {detailRecord.post_publish_ghs}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                      —
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    计划复诊
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {formatDate(detailRecord.scheduled_ghs_rescan)}
                  </Typography>
                </Grid>
              </Grid>
              <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1 }}>
                收录率与复检进展属收录台账域（发文接口无此字段），请前往「收录台账」排查。
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setDetailRecord(null)} color="inherit">
                关闭
              </Button>
              {detailRecord.publish_url && (
                <Button
                  variant="contained"
                  endIcon={<LaunchOutlined sx={{ fontSize: 14 }} />}
                  href={detailRecord.publish_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  打开原文
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 补标弹窗 */}
      <Dialog
        open={tagRecord !== null}
        onClose={() => !tagSubmitting && setTagRecord(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>补标产品</DialogTitle>
        <DialogContent>
          {tagRecord && (
            <>
              <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                {tagRecord.content_title}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
                {tagRecord.platform} · {formatDateTime(tagRecord.publish_time)}
                {tagRecord.product_name_snapshot
                  ? ` · 创建时快照「${tagRecord.product_name_snapshot}」`
                  : " · 无产品快照"}
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                label="归属产品（该发文品牌下）"
                value={tagProductId}
                onChange={(e) => setTagProductId(e.target.value)}
                disabled={tagLoading || tagProducts.length === 0}
                sx={{ mt: 0.5 }}
              >
                {tagProducts.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              {tagLoading && (
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
                  正在加载产品候选...
                </Typography>
              )}
              {!tagLoading && tagProducts.length === 0 && !tagError && (
                <Typography sx={{ fontSize: 12, color: "warning.main", mt: 1 }}>
                  该品牌下暂无产品，请先在「产品」页建档
                </Typography>
              )}
              <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1 }}>
                补标后写入 product_id 并把快照刷新为产品当前名；之后删除产品仍留名（R4）。
              </Typography>
              {tagError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {tagError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTagRecord(null)} disabled={tagSubmitting} color="inherit">
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitTag}
            disabled={tagSubmitting || !tagProductId}
          >
            {tagSubmitting ? "提交中..." : "确认补标"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功提示 */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSnackOpen(false)}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
